import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContent, type ArticleSection } from '@/mocks/article-content';

function SectionRenderer({ section }: { section: ArticleSection }) {
  switch (section.type) {
    case 'h2':
      return <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">{section.text}</h2>;
    case 'h3':
      return <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{section.text}</h3>;
    case 'p':
      return <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">{section.text}</p>;
    case 'list':
      return (
        <div className="mb-5">
          {section.itemsTitle && <p className="text-foreground-800 font-medium text-sm mb-3">{section.itemsTitle}</p>}
          <ul className="space-y-2">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-foreground-700 text-sm leading-relaxed">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-primary-500 text-sm"></i>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </div>
      );
    case 'table':
      return (
        <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary-50">
                {section.headers?.map((h, i) => (
                  <th key={i} className={`text-left p-4 font-semibold whitespace-nowrap ${i === 0 ? 'text-foreground-900' : i === 1 ? 'text-primary-700' : 'text-foreground-700'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {section.rows?.map((row, ri) => (
                <tr key={ri} className="hover:bg-background-50">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`p-4 whitespace-nowrap ${ci === 0 ? 'font-medium text-foreground-900' : ci === 1 ? 'text-primary-700' : 'text-foreground-600'}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'cta':
      return (
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-6 md:p-8 text-center mb-5">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-3">{section.text}</h2>
          <p className="text-foreground-600 text-sm mb-6 max-w-lg mx-auto">
            Visit the official store to browse the full collection and discover how Litbuy Spreadsheet can transform your workflow.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
            >
              litbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-all"
            >
              Buy It Now
            </a>
          </div>
        </div>
      );
    case 'disclaimer':
      return (
        <div className="bg-accent-50 border border-accent-200 rounded-lg p-5 mb-5">
          <p className="text-accent-800 text-sm leading-relaxed">
            <strong className="text-accent-900">Important Disclaimer:</strong> {section.text}
          </p>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const data = slug ? articleContent[slug] : undefined;

  useEffect(() => {
    if (data) {
      document.title = data.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', data.metaDescription);
      }

      const siteUrl = import.meta.env.VITE_SITE_URL;
      const canonicalUrl = `${siteUrl}/article/${slug}`;

      // Update canonical link
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonicalUrl;

      // Update last-modified
      let lastModMeta = document.querySelector('meta[name="last-modified"]');
      if (!lastModMeta) {
        lastModMeta = document.createElement('meta');
        lastModMeta.setAttribute('name', 'last-modified');
        document.head.appendChild(lastModMeta);
      }
      lastModMeta.setAttribute('content', data.updatedDate);

      const existingSchema = document.getElementById('schema-jsonld');
      if (existingSchema) {
        existingSchema.remove();
      }
      const existingHomeSchema = document.getElementById('schema-home');
      if (existingHomeSchema) {
        existingHomeSchema.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'schema-jsonld';

      const schemas = [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          '@id': `${canonicalUrl}#article`,
          headline: data.title,
          description: data.metaDescription,
          author: {
            '@type': 'Organization',
            name: data.author,
          },
          datePublished: data.updatedDate,
          dateModified: data.updatedDate,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: 'LitbuySpreadsheets',
            url: siteUrl,
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          '@id': `${canonicalUrl}#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: siteUrl,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Articles',
              item: `${siteUrl}/`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: data.title,
              item: canonicalUrl,
            },
          ],
        },
      ];

      script.textContent = JSON.stringify(schemas);
      document.head.appendChild(script);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });

    return () => {
      const s = document.getElementById('schema-jsonld');
      if (s) {
        s.remove();
      }
      const h = document.getElementById('schema-home');
      if (h) {
        h.remove();
      }
    };
  }, [data, slug]);

  if (!data) {
    return (
      <main>
        <Navbar />
        <div className="pt-28 pb-16 px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Article Not Found</h1>
          <p className="text-foreground-600 mt-2 text-sm">The article you are looking for does not exist yet. Browse our available articles below.</p>
          <Link to="/" className="inline-block mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-600 transition-all whitespace-nowrap">
            Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />

      <article>
        {/* Article Header */}
        <header className="pt-28 md:pt-32 pb-10 md:pb-14 px-4 md:px-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 cursor-pointer transition-colors">Home</Link>
              <span className="text-foreground-400">/</span>
              <Link to="/" className="hover:text-primary-600 cursor-pointer transition-colors">Articles</Link>
              <span className="text-foreground-400">/</span>
              <span className="text-foreground-700">{data.category}</span>
            </nav>

            <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4">
              {data.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {data.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </div>
                {data.author}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </div>
                {data.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </div>
                Updated: {data.updatedDate}
              </span>
            </div>
          </div>
        </header>

        {/* Article Body */}
        <div className="max-w-3xl mx-auto px-4 md:px-12 pb-12">
          {/* Introduction */}
          <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-10">
            {data.intro}
          </p>

          {/* Dynamic Sections */}
          {data.sections.map((section, index) => (
            <section key={index} className="mb-8">
              <SectionRenderer section={section} />
            </section>
          ))}

          {/* Internal Links */}
          <section className="border-t border-background-200/70 pt-8 mt-8 mb-8">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
              Explore More Litbuy Spreadsheet Resources
            </h3>
            <div className="flex flex-wrap gap-2">
              <Link to="/" className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap">
                Home Page
              </Link>
              <Link to="/article/what-is-litbuy-spreadsheet" className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap">
                Getting Started
              </Link>
              <Link to="/article/litbuy-spreadsheet-features" className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap">
                Features Guide
              </Link>
              <Link to="/article/litbuy-spreadsheet-benefits" className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap">
                Benefits
              </Link>
              <Link to="/article/litbuy-spreadsheet-vs-excel" className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap">
                Comparison Guide
              </Link>
              <Link to="/article/shopping-guide" className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap">
                Shopping Guide
              </Link>
              <Link to="/article/is-litbuy-spreadsheet-safe" className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors whitespace-nowrap">
                Safety &amp; Security
              </Link>
            </div>
          </section>

          {/* Author Section */}
          <section className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 flex flex-col sm:flex-row gap-4 items-start mt-8">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-3-line text-primary-600 text-lg"></i>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-foreground-900 text-sm">About the Author</h3>
              <p className="text-foreground-600 text-xs mt-1 leading-relaxed">
                The {data.author} is dedicated to providing accurate, well-researched, and practical information about spreadsheet tools and productivity software. Our content is created following EEAT principles to ensure readers receive trustworthy guidance they can rely on.
              </p>
            </div>
          </section>

          {/* Disclaimer */}
          <div className="mt-8 bg-foreground-50 border border-foreground-200 rounded-lg p-5">
            <p className="text-foreground-600 text-xs leading-relaxed">
              <strong>Disclaimer:</strong> This article is for informational and educational purposes only. It does not constitute professional advice, endorsement, or recommendation of any specific product. Readers should conduct their own research and consult official sources before making decisions based on the information presented. External links are provided for convenience and we are not responsible for the content of linked websites.
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}